import React from 'react';
import { Navigate, useOutletContext } from 'react-router-dom';
import { useSelector } from 'react-redux';
import CompanyDashboard from './index';
import { CHIEF_LANDING_PATHS, pickLandingPath } from '../../components/layout/navigationModules';
import { FilteredNavigationState } from '../../hooks/useFilteredNavigation';

/**
 * Chooses what `/` shows, because Home is a module a company can switch off.
 *
 * The decision is delegated to the navigation the sidebar is already drawing, rather than made from
 * a list of special cases here. That matters because Home can be switched off by any company, not
 * only an engagement-only one: whatever combination of modules a tenant is left with, `/` resolves
 * to the first page of it that this particular user can open. Role gates, module config, page
 * config, compliance country, shift swaps and billing have all been applied by then, so the target
 * can never be a route that bounces the user straight back out.
 */
const HomeRoute: React.FC = () => {
  const userId = useSelector((s: { user?: { userId?: string } }) => s.user?.userId);
  const role = useSelector((s: { user?: { role?: string } }) => s.user?.role);
  const { modules, loaded } = useOutletContext<FilteredNavigationState>();

  // Redirecting before the company's config lands would send the user to whatever the defaults
  // resolve to, then move them again a moment later.
  if (!loaded) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  // Home survives filtering only when it is enabled, so its presence is the whole test.
  if (modules.some(m => m.id === 'home')) return <CompanyDashboard />;

  // Profile is the last resort: its route has no guard, so it is reachable even by a user whose
  // sidebar is empty.
  // CO-815 — a Chief lands on a company overview, not on the Clock In page at the top of the sidebar.
  const preferred = (role ?? '').toLowerCase() === 'chief' ? CHIEF_LANDING_PATHS : [];
  const landing = pickLandingPath(modules, preferred) ?? `/profile/${userId}/personal`;
  return <Navigate to={landing} replace />;
};

export default HomeRoute;
